// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  API_URL,
  authFetch,
  fetchTeacherSubjectsWithStudents,
  fetchBreakoutRooms,
} from '../../../services/api';
import {
  createAssignment,
  fetchAssignmentById,
  updateAssignment,
  calculateGradingStats,
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_TYPES,
  SCORING_METHODS,
  uploadTaskFile,
} from '../../../services/assignments';
import { AttachmentList } from '../../../components/LearningTasks/TaskReplies';
import './Assignments.css';
import { TIMELINESS_STANDARDS, timelinessStandard } from '../../../utils/taskTimeliness';
import { canonicalSubject } from '../../../utils/progressStatus';

export default function LearningTaskCard({ taskSelections, onChooseStandards }: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { assignmentId } = useParams();
  const isEditing = Boolean(assignmentId);
  const teacherId = user?.user_id || user?.id || null;

  // Available classes & standards (loaded from live backend)
  const [availableClasses, setAvailableClasses] = useState<any>([]);
  const [availableStandards, setAvailableStandards] = useState<any>([]);
  const [loadingData, setLoadingData] = useState<any>(true);
  const [assignmentLoading, setAssignmentLoading] = useState(isEditing);
  const [existingAssignment, setExistingAssignment] = useState<any>(null);
  const [loadError, setLoadError] = useState<any>('');
  const [saveError, setSaveError] = useState<any>('');
  const [saving, setSaving] = useState<any>(false);

  // Form State
  const [selectedClassId, setSelectedClassId] = useState<any>('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedStandards, setSelectedStandards] = useState<any>([]);
  const [classGroups, setClassGroups] = useState<any>([]);
  const [groupsLoading, setGroupsLoading] = useState<any>(false);
  const [groupsError, setGroupsError] = useState<any>('');

  const [selectedStudentIds, setSelectedStudentIds] = useState<any>([]);

  // Assignment Details
  const [title, setTitle] = useState<any>('');
  const [type, setType] = useState('Assignment');
  const [assignedDate, setAssignedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  });
  const [instructions, setInstructions] = useState<any>('');
  const [scoringMethod, setScoringMethod] = useState(SCORING_METHODS.MASTERY);
  const [maxScore, setMaxScore] = useState(4);
  const [teacherNotes, setTeacherNotes] = useState<any>('');
  const [initialStatus, setInitialStatus] = useState('assigned');
  const [timeliness, setTimeliness] = useState({ enabled: false, scope: 'task', grade: '', standardCodes: [] });

  // Attachments State
  const fileInputRef = React.useRef<any>(null);
  const [attachments, setAttachments] = useState<any>([]);
  const [uploadingFiles, setUploadingFiles] = useState<any>(false);
  const [uploadError, setUploadError] = useState<any>('');
  const [linkModalOpen, setLinkModalOpen] = useState<any>(false);
  const [linkUrlInput, setLinkUrlInput] = useState<any>('');
  const [linkTitleInput, setLinkTitleInput] = useState<any>('');
  const [previewOpen, setPreviewOpen] = useState<any>(false);

  const [standardSearch, setStandardSearch] = useState<any>('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [strandFilter, setStrandFilter] = useState('all');

  const [studentSearch, setStudentSearch] = useState<any>('');

  useEffect(() => {
    if (!taskSelections) return;
    setSelectedStandards(taskSelections.map(selection => ({
      mlsCode: selection.code,
      subject: selection.subject,
      domain: selection.domain,
      strand: selection.strandDescription || selection.strand,
      friendlyTitle: selection.standardDescription || selection.code,
      description: selection.standardDescription || selection.code,
    })));
  }, [taskSelections]);

  // Load classes & standards from live backend
  useEffect(() => {
    async function initData() {
      setLoadingData(true);
      try {
        const subData = await fetchTeacherSubjectsWithStudents();
        const subjectsList = Array.isArray(subData) ? subData : subData?.subjects || [];
        if (subjectsList.length > 0) {
          const mapped = [];
          subjectsList.forEach((sub) => {
            const subName = sub.subject_name || sub.subject || 'Subject';
            (sub.classes || []).forEach((c) => {
              mapped.push({
                id: String(c.class_id || c.id),
                name: c.class_name || c.name || `${subName} - Class ${c.class_id}`,
                grade: c.grade_level ? (String(c.grade_level).toLowerCase().startsWith('grade') ? c.grade_level : `Grade ${c.grade_level}`) : '',
                subject: subName,
                students: (c.students || []).map((s) => ({
                  studentId: String(s.student_id || s.id),
                  name: s.full_name || s.name || `${s.last_name || ''}, ${s.first_name || ''}`.trim() || 'Student',
                  studentNumber: s.student_number || `STU${s.student_id}`,
                })),
              });
            });
          });
          if (mapped.length > 0) {
            setAvailableClasses(mapped);
            const preferredClass = location.state?.selectedClassId
              ? mapped.find((c) => String(c.id) === String(location.state.selectedClassId))
              : null;
            if (!assignmentId) {
              const matchingClasses = taskSelections
                ? mapped.filter(cls => canonicalSubject(cls.subject) === canonicalSubject(taskSelections[0]?.subject))
                : mapped;
              const initialClass = matchingClasses.find(cls => cls.id === preferredClass?.id) || matchingClasses[0];
              setSelectedClassId(initialClass?.id || '');
            }
          }
        }
      } catch (err: any) {
        console.warn('Could not load active classes:', err);
        setLoadError('Could not load active classes. Please reload and try again.');
      }

      if (taskSelections) {
        setLoadingData(false);
        return;
      }
      try {
        const res = await authFetch(`${API_URL}/api/progression/standards-progression-data`);
        if (!res.ok) throw new Error('Could not load standards.');
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data?.standards || [];
          if (list.length > 0) {
            const mappedStds = list.map((s) => ({
              mlsCode: s.mls_code,
              subject: s.subject || 'Literacy',
              domain: s.domain_description || s.domain || 'General',
              strand: s.strand_description || s.strand || 'General',
              friendlyTitle: s.standard_description || s.mls_code,
              description: s.standard_description || s.mls_code,
            }));
            setAvailableStandards(mappedStds);
          }
        }
      } catch (err: any) {
        console.warn('Could not load standards:', err);
        setLoadError('Could not load standards. Please reload and try again.');
      } finally {
        setLoadingData(false);
      }
    }

    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!assignmentId) return undefined;
    let cancelled = false;
    setAssignmentLoading(true);
    fetchAssignmentById(assignmentId, teacherId)
      .then((assignment) => {
        if (cancelled) return;
        if (!assignment) throw new Error('Learning task not found.');
        setExistingAssignment(assignment);
        setSelectedClassId(String(assignment.classId));
        setSelectedStudentIds((assignment.students || []).map((student) => String(student.studentId)));
        setSelectedStandards(assignment.standards || []);
        setTitle(assignment.title || '');
        setType(assignment.type || 'Assignment');
        setAssignedDate(assignment.assignedDate || '');
        setDueDate(assignment.dueDate || '');
        setInstructions(assignment.instructions || '');
        setScoringMethod(assignment.scoringMethod || SCORING_METHODS.MASTERY);
        setMaxScore(assignment.maxScore || 4);
        setTeacherNotes(assignment.teacherNotes || '');
        setInitialStatus(assignment.status || 'assigned');
        setTimeliness(assignment.timeliness || { enabled: false, scope: 'task', grade: '', standardCodes: [] });
        setAttachments(assignment.attachments || []);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error.message || 'Could not load learning task.');
      })
      .finally(() => {
        if (!cancelled) setAssignmentLoading(false);
      });
    return () => { cancelled = true; };
  }, [assignmentId, teacherId]);

  // Selected Class details
  const currentClass = useMemo(() => {
    if (existingAssignment) {
      const activeClass = availableClasses.find((c) => c.id === String(existingAssignment.classId));
      const roster = new Map((activeClass?.students || []).map((student) => [String(student.studentId), student]));
      (existingAssignment.students || []).forEach((student) => {
        roster.set(String(student.studentId), { ...student, studentId: String(student.studentId) });
      });
      return {
        id: String(existingAssignment.classId), name: existingAssignment.className,
        grade: existingAssignment.grade, subject: existingAssignment.subject,
        students: [...roster.values()],
      };
    }
    return availableClasses.find((c) => c.id === selectedClassId) || null;
  }, [availableClasses, selectedClassId, existingAssignment]);

  useEffect(() => {
    if (!taskSelections?.length || !availableClasses.length) return;
    const subject = taskSelections[0].subject;
    if (!currentClass || canonicalSubject(currentClass.subject) !== canonicalSubject(subject)) {
      setSelectedClassId(availableClasses.find(cls => canonicalSubject(cls.subject) === canonicalSubject(subject))?.id || '');
    }
  }, [taskSelections, availableClasses, currentClass]);

  useEffect(() => {
    setGroupsError('');
    setClassGroups([]);
    setSelectedGroup('all');
    setGroupsLoading(Boolean(currentClass?.id));
    if (!currentClass?.id) return undefined;
    let cancelled = false;
    fetchBreakoutRooms(currentClass.id)
      .then(groups => {
        if (!cancelled) setClassGroups(Array.isArray(groups) ? groups : []);
      })
      .catch(error => {
        if (!cancelled) setGroupsError(error.message || 'Could not load class groups. Please reload to try again.');
      })
      .finally(() => { if (!cancelled) setGroupsLoading(false); });
    return () => { cancelled = true; };
  }, [currentClass?.id]);

  // Set default students when class changes
  useEffect(() => {
    setSelectedStudentIds((existingAssignment?.students || currentClass?.students || []).map((s) => String(s.studentId)));
  }, [currentClass, existingAssignment]);

  // Standards filtered by selected class's subject
  const subjectStandards = useMemo(() => {
    if (!currentClass?.subject) return availableStandards;
    const match = availableStandards.filter(
      (s) => (s.subject || '').toLowerCase() === currentClass.subject.toLowerCase()
    );
    return match.length > 0 ? match : availableStandards;
  }, [availableStandards, currentClass]);

  // Domains & Strands for the current subject standards
  const domains = useMemo(() => {
    const set = new Set();
    subjectStandards.forEach((s) => { if (s.domain) set.add(s.domain); });
    return Array.from(set);
  }, [subjectStandards]);

  const strands = useMemo(() => {
    const set = new Set();
    subjectStandards.forEach((s) => {
      if (domainFilter === 'all' || s.domain === domainFilter) {
        if (s.strand) set.add(s.strand);
      }
    });
    return Array.from(set);
  }, [subjectStandards, domainFilter]);

  const visibleStandards = useMemo(() => {
    return subjectStandards.filter((s) => {
      if (domainFilter !== 'all' && s.domain !== domainFilter) return false;
      if (strandFilter !== 'all' && s.strand !== strandFilter) return false;
      if (standardSearch.trim()) {
        const q = standardSearch.toLowerCase().trim();
        const matchesCode = (s.mlsCode || '').toLowerCase().includes(q);
        const matchesTitle = (s.friendlyTitle || '').toLowerCase().includes(q);
        const matchesDesc = (s.description || '').toLowerCase().includes(q);
        if (!matchesCode && !matchesTitle && !matchesDesc) return false;
      }
      return true;
    });
  }, [subjectStandards, domainFilter, strandFilter, standardSearch]);

  const handleRemoveStandard = (mlsCode) => {
    setSelectedStandards(selectedStandards.filter((s) => s.mlsCode !== mlsCode));
  };

  const visibleStudents = useMemo(() => {
    let all = currentClass?.students || [];
    if (selectedGroup !== 'all') {
      const group = classGroups.find(group => String(group.breakout_room_id) === selectedGroup);
      const ids = new Set((group?.students || []).map(student => String(student.student_id)));
      all = all.filter(student => ids.has(student.studentId));
    }
    if (!studentSearch.trim()) return all;
    const q = studentSearch.toLowerCase().trim();
    return all.filter(
      (s) => (s.name || '').toLowerCase().includes(q) || (s.studentNumber || '').toLowerCase().includes(q)
    );
  }, [currentClass, selectedGroup, studentSearch, classGroups]);

  const allShownSelected = useMemo(() => {
    if (visibleStudents.length === 0) return false;
    return visibleStudents.every((stu) => selectedStudentIds.includes(stu.studentId));
  }, [visibleStudents, selectedStudentIds]);

  const selectedAssignmentStudents = useMemo(
    () => (currentClass?.students || []).filter(student => selectedStudentIds.includes(student.studentId)),
    [currentClass, selectedStudentIds],
  );

  const handleToggleStudent = (stuId) => {
    if (selectedStudentIds.includes(stuId)) {
      setSelectedStudentIds(selectedStudentIds.filter((id) => id !== stuId));
    } else {
      setSelectedStudentIds([...selectedStudentIds, stuId]);
    }
  };

  const handleToggleAllShownStudents = () => {
    if (allShownSelected) {
      // Unselect visible students
      const visibleIds = visibleStudents.map((s) => s.studentId);
      setSelectedStudentIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      // Select all visible students
      const newIds = new Set(selectedStudentIds);
      visibleStudents.forEach((s) => newIds.add(s.studentId));
      setSelectedStudentIds(Array.from(newIds));
    }
  };

  // Files are uploaded right away so students, families and co-teachers can open them from any device.
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setUploadingFiles(true);
    setUploadError('');
    try {
      for (const file of files) {
        const uploaded = await uploadTaskFile(file);
        setAttachments((prev) => [...prev, uploaded]);
      }
    } catch (err: any) {
      setUploadError(`Could not upload file: ${err.message}`);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleAddLink = () => {
    if (!linkUrlInput.trim()) return;
    setAttachments((prev) => [
      ...prev,
      {
        type: 'link',
        name: linkTitleInput.trim() || linkUrlInput.trim(),
        url: linkUrlInput.trim(),
      },
    ]);
    setLinkUrlInput('');
    setLinkTitleInput('');
    setLinkModalOpen(false);
  };

  const handleRemoveAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  // Launch the assignment and grading grid
  const handleCreateAndLaunch = async (target = 'score') => {
    if (saving) return;
    setSaveError('');
    if (uploadingFiles) {
      setSaveError('Please wait for file uploads to finish.');
      return;
    }
    if (!currentClass) {
      setSaveError('Please select an active class.');
      return;
    }
    if (taskSelections && selectedStandards.some(standard => canonicalSubject(standard.subject) !== canonicalSubject(currentClass.subject))) {
      setSaveError('Choose a class matching the selected standards subject, or go back and change standards.');
      return;
    }
    if (!title.trim()) {
      setSaveError('Please enter a Learning Task / Assignment title.');
      return;
    }
    if (selectedStandards.length === 0) {
      setSaveError('Please select at least one standard to assess.');
      return;
    }
    if (scoringMethod === SCORING_METHODS.POINTS && (!Number.isFinite(Number(maxScore)) || Number(maxScore) <= 0)) {
      setSaveError('Please enter a maximum score greater than zero.');
      return;
    }
    const timingGrade = timeliness.grade || currentClass.grade;
    const timingCodes = (timeliness.standardCodes || []).filter((code) => selectedStandards.some((std) => std.mlsCode === code));
    if (timeliness.enabled && !timelinessStandard(timingGrade)) {
      setSaveError('Choose a supported task grade band for timeliness grading.');
      return;
    }
    if (timeliness.enabled && timeliness.scope === 'components' && !timingCodes.length) {
      setSaveError('Select at least one time-sensitive standard component.');
      return;
    }

    let studentIdsToUse = selectedStudentIds;
    if (taskSelections && !studentIdsToUse.length) {
      setSaveError('Please select at least one student for this learning task.');
      return;
    }
    if (studentIdsToUse.length === 0) {
      const confirmAll = window.confirm(
        'No students were individually selected (defaulted to unselected). Would you like to assign this task to all students in the class?'
      );
      if (confirmAll) {
        studentIdsToUse = (currentClass.students || []).map((s) => s.studentId);
        setSelectedStudentIds(studentIdsToUse);
      } else {
        return;
      }
    }
    if (!studentIdsToUse.length) {
      setSaveError('This class has no students to assign. Add students before saving a learning task.');
      return;
    }

    const assignedStudents = (currentClass.students || []).filter((s) =>
      studentIdsToUse.includes(s.studentId)
    );

    const assignmentPayload = {
      title: title.trim(),
      type,
      classId: currentClass.id,
      className: currentClass.name,
      grade: currentClass.grade,
      subject: currentClass.subject,
      assignedDate,
      dueDate,
      assessmentDate: existingAssignment?.assessmentDate || dueDate,
      instructions,
      scoringMethod,
      maxScore: Number(maxScore) || 4,
      status: isEditing ? initialStatus : target === 'draft' ? 'draft' : 'assigned',
      teacherNotes,
      timeliness: { ...timeliness, grade: timingGrade, standardCodes: timingCodes },
      attachments,
      standards: selectedStandards,
      students: assignedStudents,
      ...(!isEditing ? { grades: {}, studentNotes: {} } : {}),
    };

    setSaving(true);
    try {
      const created = isEditing
        ? await updateAssignment(assignmentId, assignmentPayload, teacherId)
        : await createAssignment(assignmentPayload, teacherId);
      if (target === 'score') {
        navigate(`/teacher/assignments/${created.id}`);
      } else {
        navigate('/teacher/assignments');
      }
    } catch (err: any) {
      setSaveError(`Could not ${isEditing ? 'update' : 'create'} learning task: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loadingData || assignmentLoading) return <div role="status">Loading learning task...</div>;
  if (loadError) return <div role="alert">{loadError}</div>;
  const stats = existingAssignment ? calculateGradingStats({
    ...existingAssignment, standards: selectedStandards, students: selectedAssignmentStudents,
  }) : null;
  const hasRecordedScores = Object.values(existingAssignment?.grades || {})
    .some((studentGrades) => Object.keys(studentGrades || {}).length > 0);

  return (
    <div className="lms-assignments-container">

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button
            type="button"
            className="lms-btn-secondary lms-btn-sm"
            onClick={() => navigate('/teacher/assignments')}
            style={{ marginBottom: '8px' }}
          >
            ← Back to Learning Tasks
          </button>
          <h1 className="lms-header-title" style={{ margin: 0 }}>{isEditing ? 'Update Learning Task' : 'Create New Learning Task'}</h1>
          <p className="lms-header-subtitle" style={{ margin: '4px 0 0' }}>
            Confirm students and standards, then add learning task details.
          </p>
        </div>

      </div>

      <div className="lms-learning-task-wrapper">
          <div className="lms-learning-task-card">
            {/* Blue Header Banner */}
            <div className="lms-task-card-header">
              {isEditing ? 'Manage Learning Task' : 'Learning Task Details'}
            </div>

            <div className="lms-task-card-body">
              {saveError && <div role="alert">{saveError}</div>}
              {stats && (
                <section aria-label="Learning task progress">
                  <p>{stats.gradedCells} / {stats.totalCells} scores recorded ({stats.percentComplete}%)</p>
                  <p>Existing scores and student feedback are preserved when updating this card.</p>
                </section>
              )}
              {/* Top Row: Class & Group Dropdowns */}
              <div className="lms-task-top-row">
                <div>
                  <label className="lms-task-field-label">
                    Class:
                  </label>
                  <select
                    aria-label="Class"
                    disabled={isEditing}
                    className="lms-select"
                    style={{ width: '100%', borderColor: '#cbd5e1' }}
                    value={selectedClassId}
                    onChange={(e) => {
                      setSelectedClassId(e.target.value);
                      if (!taskSelections) setSelectedStandards([]);
                      setSelectedGroup('all');
                      setDomainFilter('all');
                      setStrandFilter('all');
                    }}
                  >
                    {isEditing && !availableClasses.some((cls) => cls.id === String(existingAssignment.classId)) && (
                      <option value={String(existingAssignment.classId)}>{existingAssignment.className}</option>
                    )}
                    <option value="">Select a class</option>
                    {availableClasses.filter(cls => !taskSelections || canonicalSubject(cls.subject) === canonicalSubject(taskSelections[0]?.subject)).map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name} ({cls.subject})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="lms-task-field-label">
                    Group:
                  </label>
                  <select
                    aria-label="Group"
                    disabled={groupsLoading || !classGroups.length}
                    className="lms-select"
                    style={{ width: '100%', borderColor: '#cbd5e1' }}
                    value={selectedGroup}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedGroup(value);
                      setStudentSearch('');
                      const group = classGroups.find(group => String(group.breakout_room_id) === value);
                      const ids = new Set((group?.students || []).map(student => String(student.student_id)));
                      setSelectedStudentIds((currentClass?.students || [])
                        .filter(student => value === 'all' || ids.has(student.studentId))
                        .map(student => student.studentId));
                    }}
                  >
                    <option value="all">All Students ({currentClass?.students?.length || 0})</option>
                    {classGroups.map(group => (
                      <option key={group.breakout_room_id} value={String(group.breakout_room_id)}>
                        {group.name || `Group ${group.breakout_room_id}`} ({group.students?.length || 0})
                      </option>
                    ))}
                  </select>
                </div>
                {groupsError && <p role="alert">{groupsError}</p>}
              </div>

              {/* STUDENT(S) Section */}
              <div className="lms-task-section">
                <div className="lms-task-section-header">
                  <span className="lms-task-section-title">STUDENT(S):</span>
                  <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                    Showing {visibleStudents.length} of {currentClass?.students?.length || 0} students
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <input
                    aria-label="Search students"
                    type="text"
                    className="lms-search-input"
                    style={{ flex: '1 1 220px' }}
                    placeholder="Search students..."
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 700, color: '#334155', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={allShownSelected}
                      onChange={handleToggleAllShownStudents}
                    />
                    <span>all shown students</span>
                  </label>
                </div>

                <div className="lms-task-student-grid">
                  {visibleStudents.map((stu) => {
                    const isSelected = selectedStudentIds.includes(stu.studentId);
                    return (
                      <label
                        key={stu.studentId}
                        className={`lms-task-student-item ${isSelected ? 'selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleStudent(stu.studentId)}
                        />
                        <span>{stu.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* SELECTED STANDARDS Section */}
              <div className="lms-task-section">
                <div className="lms-task-section-header">
                  <span className="lms-task-section-title">SELECTED STANDARDS</span>
                  <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>
                    {selectedStandards.length} standard{selectedStandards.length === 1 ? '' : 's'} linked to data points
                  </span>
                </div>

                {taskSelections && <button type="button" className="lms-btn-secondary"
                  onClick={onChooseStandards}>← Change standards</button>}
                {!taskSelections && <>
                {/* Domain & Strand Filters + Search */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
                  <select
                    className="lms-select"
                    style={{ flex: '1 1 180px', fontSize: '0.82rem' }}
                    value={domainFilter}
                    onChange={(e) => {
                      setDomainFilter(e.target.value);
                      setStrandFilter('all');
                    }}
                  >
                    <option value="all">All Domains ({domains.length})</option>
                    {domains.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>

                  <select
                    className="lms-select"
                    style={{ flex: '1 1 180px', fontSize: '0.82rem' }}
                    value={strandFilter}
                    onChange={(e) => setStrandFilter(e.target.value)}
                  >
                    <option value="all">All Strands ({strands.length})</option>
                    {strands.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>

                  <input
                    type="text"
                    className="lms-search-input"
                    style={{ flex: '1 1 160px', fontSize: '0.82rem' }}
                    placeholder="Search standard code or description..."
                    value={standardSearch}
                    onChange={(e) => setStandardSearch(e.target.value)}
                  />
                </div>

                {/* Available Standards Quick-Add Dropdown */}
                <div style={{ marginBottom: '10px' }}>
                  <select
                    aria-label="Add standard"
                    className="lms-select"
                    style={{ width: '100%', borderColor: '#2563eb' }}
                    value=""
                    onChange={(e) => {
                      const code = e.target.value;
                      if (code) {
                        const found = subjectStandards.find((s) => s.mlsCode === code);
                        if (found && !selectedStandards.some((s) => s.mlsCode === code)) {
                          setSelectedStandards([...selectedStandards, found]);
                        }
                      }
                    }}
                  >
                    <option value="">+ Click to add standard from {currentClass?.subject || 'Subject'}...</option>
                    {visibleStandards.map((std) => (
                      <option key={std.mlsCode} value={std.mlsCode}>
                        {std.mlsCode} · {std.friendlyTitle || std.description}
                      </option>
                    ))}
                  </select>
                </div>

                </>}
                {/* Selected Standards Bullets Box */}
                <div className="lms-task-standards-box">
                  {selectedStandards.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', padding: '16px' }}>
                      No standards selected yet. Choose one from the dropdown above.
                    </div>
                  ) : (
                    selectedStandards.map((std) => (
                      <div key={std.mlsCode} className="lms-task-standard-bullet">
                        <div>
                          <strong>• {std.mlsCode}</strong> {std.friendlyTitle || std.description}
                        </div>
                        {!taskSelections && <button
                          type="button"
                          className="remove-btn"
                          onClick={() => handleRemoveStandard(std.mlsCode)}
                          title="Remove standard"
                        >
                          ✕
                        </button>}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <p role="status" aria-label="Task selection confirmation">
                {currentClass?.name || 'No class selected'} · {selectedGroup === 'all' ? 'All students' : classGroups.find(group => String(group.breakout_room_id) === selectedGroup)?.name}
                {' · '}{selectedStudentIds.length} student{selectedStudentIds.length === 1 ? '' : 's'} selected
                {' · '}{selectedStandards.length} standard{selectedStandards.length === 1 ? '' : 's'} selected
              </p>

              {/* Title & Description */}
              <div className="lms-form-group">
                <label className="lms-task-field-label">Learning Task Title</label>
                <input
                  type="text"
                  className="lms-input"
                  aria-label="Learning Task Title"
                  placeholder="Read to Self -Day 4"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div className="lms-form-group">
                <label className="lms-task-field-label">Learning Task Description</label>
                <textarea
                  className="lms-textarea"
                  rows={3}
                  placeholder="Enter instructions, learning objectives, or prompts..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                />
              </div>

              {/* Dates Row */}
              <div className="lms-task-dates-row">
                <div>
                  <label className="lms-task-field-label">Assigned Date</label>
                  <input
                    type="date"
                    className="lms-input"
                    value={assignedDate}
                    onChange={(e) => setAssignedDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="lms-task-field-label" htmlFor="task-due-date">Due Date</label>
                  <input
                    id="task-due-date"
                    type="date"
                    className="lms-input"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Select Type of Data */}
              <div>
                <label className="lms-task-field-label">Select Type of Data</label>
                <div className="lms-task-pill-group">
                  {[...new Set(['Observation', 'Assignment', 'Assessment', ...ASSIGNMENT_TYPES.map((item) => item.value)])].map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`lms-task-pill-btn ${type === t ? 'active' : ''}`}
                      onClick={() => setType(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lms-form-group">
                <label className="lms-task-field-label">
                  <input type="checkbox" checked={timeliness.enabled}
                    onChange={(e) => setTimeliness({ ...timeliness, enabled: e.target.checked })} />
                  {' '}Track time-sensitive submission
                </label>
                {timeliness.enabled && (
                  <div>
                    <label className="lms-task-field-label">Timeliness grade band
                      <select className="lms-select" value={timeliness.grade || (timelinessStandard(currentClass?.grade) ? currentClass.grade : '')}
                        onChange={(e) => setTimeliness({ ...timeliness, grade: e.target.value })}>
                        <option value="">Choose grade band</option>
                        {currentClass?.grade && timelinessStandard(currentClass.grade) && !Object.keys(TIMELINESS_STANDARDS).includes(currentClass.grade) && (
                          <option value={currentClass.grade}>{currentClass.grade}</option>
                        )}
                        {Object.keys(TIMELINESS_STANDARDS).map((band) => <option key={band} value={band}>{band}</option>)}
                      </select>
                    </label>
                    <p>Linked Success Skills: {timelinessStandard(timeliness.grade || currentClass?.grade) || 'Select a grade band'}</p>
                    <label className="lms-task-field-label">Track timing for
                      <select className="lms-select" value={timeliness.scope}
                        onChange={(e) => setTimeliness({ ...timeliness, scope: e.target.value })}>
                        <option value="task">Whole task</option>
                        <option value="components">Selected standard components</option>
                      </select>
                    </label>
                    {timeliness.scope === 'components' && selectedStandards.map((standard) => (
                      <label key={standard.mlsCode} style={{ display: 'block' }}>
                        <input type="checkbox" checked={timeliness.standardCodes.includes(standard.mlsCode)}
                          onChange={(e) => setTimeliness({
                            ...timeliness,
                            standardCodes: e.target.checked
                              ? [...timeliness.standardCodes, standard.mlsCode]
                              : timeliness.standardCodes.filter((code) => code !== standard.mlsCode),
                          })} />
                        {' '}Time-sensitive: {standard.mlsCode}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="lms-form-group">
                <label className="lms-task-field-label" htmlFor="task-scoring-method">Scoring Method</label>
                <select id="task-scoring-method" className="lms-select" value={scoringMethod}
                  disabled={hasRecordedScores}
                  onChange={(e) => setScoringMethod(e.target.value)}>
                  <option value={SCORING_METHODS.MASTERY}>Academic Eye 4-Level Mastery</option>
                  <option value={SCORING_METHODS.POINTS}>Points</option>
                  <option value={SCORING_METHODS.RUBRIC}>Rubric</option>
                </select>
                {scoringMethod === SCORING_METHODS.POINTS && (
                  <label className="lms-task-field-label">Maximum Score
                    <input type="number" min="1" className="lms-input" value={maxScore}
                      disabled={hasRecordedScores} onChange={(e) => setMaxScore(e.target.value)} />
                  </label>
                )}
              </div>
              <div className="lms-form-group">
                <label className="lms-task-field-label" htmlFor="task-teacher-notes">Teacher Notes / Rubric Guide</label>
                <textarea id="task-teacher-notes" className="lms-textarea" rows={2}
                  value={teacherNotes} onChange={(e) => setTeacherNotes(e.target.value)} />
              </div>
              {isEditing && (
                <div className="lms-form-group">
                  <label className="lms-task-field-label" htmlFor="task-status">Task Status</label>
                  <select id="task-status" className="lms-select" value={initialStatus}
                    onChange={(e) => setInitialStatus(e.target.value)}>
                    {ASSIGNMENT_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Add Attachments */}
              <div style={{ marginTop: '14px' }}>
                <label className="lms-task-field-label">Add</label>
                <div className="lms-task-attachments-row">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    multiple
                    aria-label="Attach files"
                    onChange={handleFileUpload}
                  />
                  <button
                    type="button"
                    className="lms-btn-secondary lms-btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFiles}
                  >
                    {uploadingFiles ? 'Uploading...' : '📄 + File'}
                  </button>
                  <button
                    type="button"
                    className="lms-btn-secondary lms-btn-sm"
                    onClick={() => setLinkModalOpen(true)}
                  >
                    🔗 + Link
                  </button>

                  <AttachmentList attachments={attachments} taskId={assignmentId} onRemove={handleRemoveAttachment} />
                </div>
                {uploadError && <p role="alert" style={{ color: '#b91c1c', fontSize: '0.82rem', margin: '6px 0 0' }}>{uploadError}</p>}
              </div>

              {/* Action Buttons Row */}
              <div className="lms-task-actions-row">
                {!isEditing && (
                  <button type="button" className="lms-btn-secondary" disabled={saving}
                    onClick={() => handleCreateAndLaunch('draft')}>Save Draft</button>
                )}
                <button
                  type="button"
                  className="lms-btn-secondary"
                  disabled={saving}
                  onClick={() => setPreviewOpen(true)}
                >
                  Preview Assignment
                </button>
                <button
                  type="button"
                  className="lms-btn-secondary"
                  disabled={saving}
                  onClick={() => handleCreateAndLaunch('publish')}
                >
                  {isEditing ? 'Save Changes' : 'Publish'}
                </button>
                <button
                  type="button"
                  className="lms-btn-primary"
                  disabled={saving}
                  style={{ background: '#2e5797', borderColor: '#1e3a8a' }}
                  onClick={() => handleCreateAndLaunch('score')}
                >
                  {isEditing ? 'Save and Score' : 'Publish and Score'}
                </button>
              </div>
            </div>
          </div>
        </div>

      {previewOpen && (
        <div className="lms-modal-backdrop" onClick={() => setPreviewOpen(false)}>
          <div
            className="lms-modal-card"
            style={{ maxWidth: '640px', width: '92%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <div>
                <span className="lms-badge lms-badge-type">{type} Preview</span>
                <h2 style={{ fontSize: '1.3rem', color: '#1e3a8a', margin: '6px 0 2px' }}>{title || '(Untitled Learning Task)'}</h2>
                <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
                  {currentClass?.name || 'Class'} • Assigned: {assignedDate} • Due: {dueDate}
                </div>
              </div>
              <button
                type="button"
                className="lms-btn-secondary lms-btn-sm"
                onClick={() => setPreviewOpen(false)}
              >
                ✕ Close
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                Instructions / Description
              </h4>
              <p style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', fontSize: '0.9rem', lineHeight: 1.5, color: '#334155', margin: 0 }}>
                {instructions || 'No special instructions provided.'}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                Assessed Standards ({selectedStandards.length})
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {selectedStandards.map((std) => (
                  <div key={std.mlsCode} style={{ background: '#eff6ff', padding: '8px 12px', borderRadius: '6px', fontSize: '0.85rem' }}>
                    <strong>{std.mlsCode}</strong>: {std.friendlyTitle || std.description}
                  </div>
                ))}
              </div>
            </div>

            {timeliness.enabled && (
              <p>Time-sensitive submission: {timeliness.scope === 'components' ? timeliness.standardCodes.join(', ') : 'Whole task'}.
                {' '}Success Skills: {timelinessStandard(timeliness.grade || currentClass?.grade) || 'Select a grade band'}.
                {' '}Default: On Time.</p>
            )}
            <section aria-label="Assignment preview students" style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                Assigned Students ({selectedAssignmentStudents.length})
              </h4>
              {selectedAssignmentStudents.length > 0 ? (
                <ul style={{ margin: 0, padding: '12px 12px 12px 30px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.85rem', color: '#334155', overflowWrap: 'anywhere' }}>
                  {selectedAssignmentStudents.map(student => (
                    <li key={student.studentId} style={{ padding: '4px 0' }}>{student.name}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  {taskSelections ? 'No students selected. Select students before publishing.' : 'No students selected. Publishing will ask whether to assign this task to all students in the class.'}
                </p>
              )}
            </section>

            {attachments.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '0.82rem', textTransform: 'uppercase', color: '#64748b', marginBottom: '6px' }}>
                  Attachments & Resources
                </h4>
                <AttachmentList attachments={attachments} taskId={assignmentId} />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
              <button
                type="button"
                className="lms-btn-secondary"
                onClick={() => setPreviewOpen(false)}
              >
                Close Preview
              </button>
              <button
                type="button"
                className="lms-btn-primary"
                disabled={saving}
                style={{ background: '#2e5797', borderColor: '#1e3a8a' }}
                onClick={() => {
                  setPreviewOpen(false);
                  handleCreateAndLaunch('score');
                }}
              >
                {isEditing ? 'Save and Score' : 'Publish and Score'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD LINK MODAL                                                         */}
      
      {linkModalOpen && (
        <div className="lms-modal-backdrop" onClick={() => setLinkModalOpen(false)}>
          <div
            className="lms-modal-card"
            style={{ maxWidth: '440px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 14px', fontSize: '1.1rem', color: '#1e3a8a' }}>
              🔗 Add Resource Link
            </h3>
            <div style={{ marginBottom: '12px' }}>
              <label className="lms-task-field-label">Resource URL</label>
              <input
                type="url"
                className="lms-input"
                placeholder="https://example.com/reading-materials"
                value={linkUrlInput}
                onChange={(e) => setLinkUrlInput(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label className="lms-task-field-label">Display Title (Optional)</label>
              <input
                type="text"
                className="lms-input"
                placeholder="e.g. Chapter 3 Excerpt"
                value={linkTitleInput}
                onChange={(e) => setLinkTitleInput(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                className="lms-btn-secondary lms-btn-sm"
                onClick={() => setLinkModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lms-btn-primary lms-btn-sm"
                onClick={handleAddLink}
              >
                Add Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
